import { datadogRum } from "@datadog/browser-rum";
import * as Sentry from "@sentry/react";
import { Role } from "@/apollo/types";
import { RoleProvider } from "@/features/roleAccess";
import { useAtom } from "jotai";
import { useMixpanel } from "@/monitoring/mixpanel";
import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from "react";

import { AccountType, useUserAccounts } from "../UserProvider";
import { setCurrentAccountIdScope } from "./accountScope";
import { mostRecentAccountId } from "./mostRecentAccount";

export const AccountContext = createContext<{
  account: AccountType;
  role: Role;
} | null>(null);

/**
 * The current account id, or null outside an `AccountProvider` — which is a
 * real position for some components: the workspace switcher also renders on
 * `/workspaces/create`, where no workspace is selected yet.
 *
 * Prefer `useCurrentAccount()` where the caller is always inside the provider;
 * it throws rather than quietly reporting no account.
 */
export function useCurrentAccountIdValue() {
  return useContext(AccountContext)?.account.id ?? null;
}

type AccountProviderProps = {
  accountId?: string;
  onInvalidAccount?: (accountId: string) => void;
};

export const AccountProvider = ({
  accountId,
  onInvalidAccount,
  children,
}: PropsWithChildren<AccountProviderProps>) => {
  const accounts = useUserAccounts();

  const currentAccount: AccountType | null = useMemo(
    () => accounts.find((account) => account.id === accountId) ?? null,
    [accounts, accountId],
  );

  const [, setMostRecentAccountId] = useAtom(mostRecentAccountId);

  // Role and permissions are resolved per account as part of the
  // CurrentUserWithAccounts query — no extra account-scoped request needed.
  const userRole = currentAccount?.currentUserRole as Role | undefined;
  const userPermissions = currentAccount?.currentUserPermissions ?? [];

  useEffect(() => {
    if (accountId === undefined) return;
    if (accounts.length === 0) return;

    const isValidAccountId = (accountId: string) => {
      return accounts.find((x) => x.id === accountId) !== undefined;
    };

    if (isValidAccountId(accountId)) {
      setMostRecentAccountId(accountId);
    } else {
      onInvalidAccount?.(accountId);
      return;
    }
  }, [accountId, accounts, onInvalidAccount, setMostRecentAccountId]);

  const mixpanel = useMixpanel();
  useEffect(() => {
    if (currentAccount === null) return;
    devLog(currentAccount);

    const { id, name, slug, dataWarehouse } = currentAccount;
    Sentry.setContext("workspace", {
      id,
      name,
      slug,
      integrationId: dataWarehouse?.integrationId,
    });

    datadogRum.setGlobalContextProperty("workspace", {
      id,
      name,
      slug,
      integrationId: dataWarehouse?.integrationId,
    });

    mixpanel.set_group("workspace", slug);
    mixpanel.register({
      workspace_id: id,
      workspace_name: name,
      workspace_slug: slug,
      workspace_integration_id: dataWarehouse?.integrationId ?? null,
    });
  }, [currentAccount, mixpanel]);

  const ctx = useMemo(() => {
    if (currentAccount === null || !userRole) return null;
    return {
      account: currentAccount,
      role: userRole,
    };
  }, [currentAccount, userRole]);

  if (!ctx) {
    return null;
  }

  // Written during render, on purpose: children fire their queries as they
  // render, and those requests need the account-context header to already be
  // correct. Publishing from an effect instead would mean either withholding
  // the whole subtree for an extra render pass, as this provider used to, or
  // letting the first requests go out unscoped. The write is idempotent and
  // derived only from props, so a repeated or discarded render cannot leave it
  // holding a value the tree is not about to render with.
  setCurrentAccountIdScope(ctx.account.id);

  return (
    <AccountContext.Provider value={ctx}>
      <RoleProvider role={ctx.role} permissions={userPermissions}>
        {children}
      </RoleProvider>
    </AccountContext.Provider>
  );
};

const useAccountContext = () => {
  const ctx = useContext(AccountContext);
  if (ctx == null) {
    throw new Error("AccountProvider not available");
  }
  return ctx;
};

export function useCurrentAccount() {
  return useAccountContext().account;
}

export const useUserRole = () => {
  return useAccountContext().role;
};

export function useDataWarehouse() {
  const dwh = useAccountContext().account.dataWarehouse;
  return {
    ...(dwh ?? {}),
    isManagedDataWarehouse: dwh?.integrationId === "weld-bigquery",
  };
}

function devLog(account: AccountType) {
  if (import.meta.env.DEV) {
    /* eslint-disable no-console */
    console.group("workspace");
    console.debug(account);
    console.groupEnd();
    /* eslint-enable no-console */
  }
}
